import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface Article {
  id: string;
  title: string;
  source: string;
  date: string;
  snippet: string;
  url: string;
  sentiment?: "positive" | "negative" | "neutral";
}

// Fallback mock data in case the API fails
export const MOCK_ARTICLES: Article[] = [
  {
    id: '1',
    title: 'Company announces new product line',
    source: 'Tech News',
    date: '2025-03-28',
    snippet: 'The company revealed its latest innovation that aims to revolutionize the industry with groundbreaking features...',
    url: 'https://example.com/news/1',
    sentiment: 'positive'
  },
  {
    id: '2',
    title: 'Financial results exceed expectations',
    source: 'Business Weekly',
    date: '2025-03-15',
    snippet: 'Quarterly reports show strong performance despite challenging market conditions, with revenue growth of 15%...',
    url: 'https://example.com/news/2',
    sentiment: 'positive'
  },
  {
    id: '3',
    title: 'New executive team appointments',
    source: 'Corporate Insider',
    date: '2025-03-10',
    snippet: 'Strategic changes to leadership as the company prepares for its next phase of growth and expansion...',
    url: 'https://example.com/news/3',
    sentiment: 'neutral'
  },
  {
    id: '4',
    title: 'Industry analysts update company outlook',
    source: 'Market Watch',
    date: '2025-03-05',
    snippet: "Following recent developments, market analysts have revised their forecasts for the company's performance...",
    url: 'https://example.com/news/4',
    sentiment: 'neutral'
  },
  {
    id: '5',
    title: 'Company announces layoffs amid restructuring',
    source: 'Business Daily',
    date: '2025-02-28',
    snippet: "The company has announced a reduction in workforce as part of its ongoing restructuring efforts...",
    url: 'https://example.com/news/5',
    sentiment: 'negative'
  }
];

export const useMediaMonitoring = (companyName: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState<Article[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Function to fetch articles from the edge function
  const fetchArticles = async (category = 'all') => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('media-monitoring', {
        body: { 
          companyName: companyName,
          category: category
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data.articles && Array.isArray(data.articles)) {
        setArticles(data.articles);
      } else {
        throw new Error("Invalid response from API");
      }
    } catch (err) {
      console.error("Error fetching articles:", err);
      setError("Failed to fetch news articles. Using sample data instead.");
      // Use mock data as fallback
      setArticles(MOCK_ARTICLES);
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    if (companyName) {
      fetchArticles(selectedCategory);
    }
  }, [companyName]);
  
  // Handle category change
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    fetchArticles(category);
  };
  
  // Handle manual refresh
  const handleRefresh = () => {
    fetchArticles(selectedCategory);
  };

  // Display articles for the current tab
  const displayArticles = articles.length > 0 ? articles : MOCK_ARTICLES;

  return {
    isLoading,
    selectedCategory,
    articles: displayArticles,
    error,
    handleCategoryChange,
    handleRefresh
  };
};
